import {
  createContext,
  type FormEvent,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  Link,
  NavLink,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams
} from "react-router-dom";
import {
  AnimatePresence,
  motion,
  useMotionValueEvent,
  useReducedMotion,
  useScroll,
  useTransform
} from "framer-motion";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { builderItems, faqs, journalPosts, products, type Product } from "./data";
import AdminApp from "./admin/Admin";
import {
  addBuilderSubmission,
  addEnquiry,
  addNewsletterSignup,
  createOrder
} from "./admin/data";
import {
  atelierEase,
  motionDurations,
  revealTransition,
  revealViewport,
  staggerContainer,
  staggerItem
} from "./motion";

type CartLine = { product: Product; quantity: number };
type CartContextValue = {
  lines: CartLine[];
  count: number;
  total: number;
  add: (product: Product) => void;
  remove: (slug: string) => void;
  change: (slug: string, quantity: number) => void;
  clear: () => void;
};

const CartContext = createContext<CartContextValue | null>(null);

function useCart() {
  const value = useContext(CartContext);
  if (!value) throw new Error("CartContext is not available");
  return value;
}

function CartProvider({ children }: { children: ReactNode }) {
  const [lines, setLines] = useState<CartLine[]>(() => {
    try {
      return JSON.parse(localStorage.getItem("trio-cart") ?? "[]") as CartLine[];
    } catch {
      return [];
    }
  });

  useEffect(() => localStorage.setItem("trio-cart", JSON.stringify(lines)), [lines]);

  const value = useMemo<CartContextValue>(
    () => ({
      lines,
      count: lines.reduce((sum, line) => sum + line.quantity, 0),
      total: lines.reduce((sum, line) => sum + line.product.price * line.quantity, 0),
      add: (product) =>
        setLines((current) => {
          const existing = current.find((line) => line.product.slug === product.slug);
          return existing
            ? current.map((line) =>
                line.product.slug === product.slug
                  ? { ...line, quantity: line.quantity + 1 }
                  : line
              )
            : [...current, { product, quantity: 1 }];
        }),
      remove: (slug) =>
        setLines((current) => current.filter((line) => line.product.slug !== slug)),
      change: (slug, quantity) =>
        setLines((current) =>
          quantity <= 0
            ? current.filter((line) => line.product.slug !== slug)
            : current.map((line) =>
                line.product.slug === slug ? { ...line, quantity } : line
              )
        ),
      clear: () => setLines([])
    }),
    [lines]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

function Dots({ small = false }: { small?: boolean }) {
  return (
    <span className={`dots ${small ? "dots--small" : ""}`} aria-hidden="true">
      <i />
      <i />
      <i />
    </span>
  );
}

function AmbientVideo({
  src,
  poster,
  alt,
  descriptionId
}: {
  src: string;
  poster: string;
  alt: string;
  descriptionId: string;
}) {
  const reduced = useReducedMotion();
  const [ready, setReady] = useState(false);

  return (
    <div className="ambient-media">
      <img src={poster} alt={reduced ? alt : ""} width="720" height="1280" />
      {!reduced && (
        <>
          <video
            className={ready ? "is-ready" : ""}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster={poster}
            aria-describedby={descriptionId}
            onCanPlay={() => setReady(true)}
            onError={() => setReady(false)}
          >
            <source src={src} type="video/mp4" />
          </video>
          <span className="sr-only" id={descriptionId}>{alt}</span>
        </>
      )}
    </div>
  );
}

type RevealDirection = "up" | "left" | "right" | "scale" | "wipe";

function Reveal({
  children,
  className,
  delay = 0,
  direction = "up"
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
  direction?: RevealDirection;
}) {
  const reduced = useReducedMotion();
  const initial = {
    up: { opacity: 0, y: 30 },
    left: { opacity: 0, x: -34 },
    right: { opacity: 0, x: 34 },
    scale: { opacity: 0, scale: 0.965 },
    wipe: { opacity: 1, clipPath: "inset(0 100% 0 0)" }
  }[direction];
  const visible = {
    opacity: 1,
    x: 0,
    y: 0,
    scale: 1,
    clipPath: "inset(0 0% 0 0)"
  };

  return (
    <motion.div
      className={className}
      initial={reduced ? false : initial}
      whileInView={visible}
      viewport={revealViewport}
      transition={{ ...revealTransition, delay }}
    >
      {children}
    </motion.div>
  );
}

function Logo({ compact = false }: { compact?: boolean }) {
  return (
    <Link className={`logo ${compact ? "logo--compact" : ""}`} to="/" aria-label="Trio Atelier Co. home">
      <img
        src="/logo-mark.png"
        srcSet="/apple-touch-icon.png 180w, /logo-mark.png 1254w"
        sizes="64px"
        alt=""
        width="64"
        height="64"
      />
      {!compact && (
        <span>
          <b>TRIO ATELIER</b>
          <small>CURATED GIFT HAMPERS</small>
        </span>
      )}
    </Link>
  );
}

const navItems = [
  ["Shop", "/shop"],
  ["Build a hamper", "/build-a-hamper"],
  ["Our story", "/our-story"],
  ["Journal", "/journal"]
] as const;

function Header() {
  const { count } = useCart();
  const [open, setOpen] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem("trio-theme") === "dark");
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const reduced = useReducedMotion();
  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, "change", (latest) => {
    const next = latest > 18;
    setScrolled((current) => (current === next ? current : next));
  });

  useEffect(() => {
    document.documentElement.dataset.theme = dark ? "dark" : "light";
    localStorage.setItem("trio-theme", dark ? "dark" : "light");
  }, [dark]);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  return (
    <>
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <div className="shipping-bar">
        <span>Complimentary UK delivery over £75</span>
        <Dots small />
        <span>Prepared with care in Leeds</span>
      </div>
      <header className={`site-header ${scrolled ? "site-header--scrolled" : ""}`}>
        <Logo />
        <nav className="desktop-nav" aria-label="Main navigation">
          {navItems.map(([label, href]) => (
            <NavLink key={href} to={href}>
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="header-actions">
          <button
            className="icon-button"
            type="button"
            aria-label={`Use ${dark ? "light" : "dark"} theme`}
            onClick={() => setDark((current) => !current)}
          >
            {dark ? "☼" : "☾"}
          </button>
          <Link className="basket-link" to="/cart" aria-label={`Basket with ${count} items`}>
            Basket <motion.span key={count} initial={reduced ? false : { scale: 0.7 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 420, damping: 20 }}>{count}</motion.span>
          </Link>
          <button
            className="menu-button"
            type="button"
            aria-label={open ? "Close navigation" : "Open navigation"}
            aria-expanded={open}
            aria-controls="mobile-navigation"
            onClick={() => setOpen((current) => !current)}
          >
            <span />
            <span />
          </button>
        </div>
      </header>
      <AnimatePresence>
        {open && (
          <>
            <motion.button
              type="button"
              className="mobile-nav-backdrop"
              aria-label="Close navigation"
              onClick={() => setOpen(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />
            <motion.nav
              className={`mobile-nav ${scrolled ? "mobile-nav--scrolled" : ""}`}
              id="mobile-navigation"
              aria-label="Mobile navigation"
              initial={reduced ? false : { opacity: 0, clipPath: "inset(0 0 100% 0)" }}
              animate={{ opacity: 1, clipPath: "inset(0 0 0% 0)" }}
              exit={{ opacity: 0, clipPath: "inset(0 0 100% 0)" }}
              transition={{ duration: motionDurations.route, ease: atelierEase }}
            >
              {[...navItems, ["Corporate gifting", "/corporate-gifting"] as const, ["Contact", "/contact"] as const].map(
                ([label, href], index) => (
                  <motion.div key={href} initial={reduced ? false : { opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.045 }}>
                    <NavLink to={href} onClick={() => setOpen(false)}>{label}</NavLink>
                  </motion.div>
                )
              )}
            </motion.nav>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

function ButtonLink({
  to,
  children,
  secondary = false
}: {
  to: string;
  children: ReactNode;
  secondary?: boolean;
}) {
  return (
    <Link className={`button ${secondary ? "button--secondary" : ""}`} to={to}>
      {children} <span aria-hidden="true">↗</span>
    </Link>
  );
}

function SectionIntro({
  eyebrow,
  title,
  text,
  light = false
}: {
  eyebrow: string;
  title: string;
  text?: string;
  light?: boolean;
}) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      className={`section-intro ${light ? "section-intro--light" : ""}`}
      variants={staggerContainer}
      initial={reduced ? false : "hidden"}
      whileInView="visible"
      viewport={revealViewport}
    >
      <motion.p className="eyebrow" variants={staggerItem}>
        <Dots small /> {eyebrow}
      </motion.p>
      <motion.h2 variants={staggerItem}>{title}</motion.h2>
      {text && <motion.p className="lede" variants={staggerItem}>{text}</motion.p>}
    </motion.div>
  );
}

function Hero() {
  const reduced = useReducedMotion();
  const heroRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const noteY = useTransform(scrollYProgress, [0, 1], [0, -26]);
  const lines = [
    "For the friend who needs reminding how wonderful she is.",
    "For the person starting a new chapter.",
    "For the one who has had a difficult week.",
    "For the daughter who deserves a little surprise.",
    "For the person you can’t quite find the words for."
  ];

  return (
    <section className="hero" ref={heroRef}>
      <div className="hero-copy">
        <p className="overline">GIFTING, THE WAY IT SHOULD FEEL</p>
        <div className="hero-lines">
          {lines.map((line, index) => (
            <motion.p
              key={line}
              initial={reduced ? false : { opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: motionDurations.reveal, delay: index * 0.09, ease: atelierEase }}
            >
              <Dots small />
              {line}
            </motion.p>
          ))}
        </div>
        <div className="hero-ending">
          <h1>For the moments words aren’t quite enough.</h1>
          <p>
            Emotion-led gift hampers, thoughtfully curated in our Leeds studio and sent across the UK.
          </p>
          <div className="button-row">
            <ButtonLink to="/shop">Shop the collection</ButtonLink>
            <ButtonLink to="/build-a-hamper" secondary>
              Build your own
            </ButtonLink>
          </div>
        </div>
      </div>
      <div className="hero-visual">
        <div className="hero-media">
          <AmbientVideo
            src="/assets/hero-hamper.mp4"
            poster="/assets/hero-hamper-poster.jpg"
            alt="Open cream hamper with tea, candle, chocolate and a sage ribbon — a hand reaching to take it"
            descriptionId="hero-media-description"
          />
        </div>
        <motion.span className="image-note" style={reduced ? undefined : { y: noteY }}>01 — Curated with intention</motion.span>
      </div>
    </section>
  );
}

function ValuesStrip() {
  const reduced = useReducedMotion();
  return (
    <motion.section
      className="values-strip"
      aria-label="Our values"
      variants={staggerContainer}
      initial={reduced ? false : "hidden"}
      whileInView="visible"
      viewport={revealViewport}
    >
      {["Thoughtfulness", "Intention", "Quality", "The experience", "Emotion"].map(
        (value, index) => (
          <motion.span key={value} variants={staggerItem}>
            <small>0{index + 1}</small>
            {value}
          </motion.span>
        )
      )}
    </motion.section>
  );
}

function ProductCard({ product, large = false, index = 0 }: { product: Product; large?: boolean; index?: number }) {
  const { add } = useCart();
  const [added, setAdded] = useState(false);
  const reduced = useReducedMotion();

  const addItem = () => {
    add(product);
    setAdded(true);
    window.setTimeout(() => setAdded(false), 1400);
  };

  return (
    <article className={`product-card product-card--${product.accent} ${large ? "product-card--large" : ""}`}>
      <Link className="product-image" to={`/shop/product/${product.slug}`}>
        <img
          src={product.image}
          alt={`${product.name} curated gift hamper`}
          loading={index < 3 ? "eager" : "lazy"}
        />
        <Dots small />
      </Link>
      <motion.div
        className="product-copy"
        initial={reduced ? false : { opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={revealViewport}
        transition={{ ...revealTransition, delay: (index % 3) * 0.07 }}
      >
        <p className="eyebrow">{product.eyebrow}</p>
        <h3>
          <Link to={`/shop/product/${product.slug}`}>{product.name}</Link>
        </h3>
        <p>{product.description}</p>
        <div className="product-meta">
          <span>£{product.price}</span>
          <button type="button" onClick={addItem} aria-live="polite">
            <AnimatePresence mode="wait" initial={false}>
              <motion.span key={added ? "added" : "add"} initial={reduced ? false : { opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }} transition={{ duration: motionDurations.quick }}>
                {added ? "Added · ✓" : "Quick add +"}
              </motion.span>
            </AnimatePresence>
          </button>
        </div>
      </motion.div>
    </article>
  );
}

function FeaturedCollections() {
  return (
    <section className="section featured-section">
      <SectionIntro
        eyebrow="A gift for how it feels"
        title="Begin with the person, not the occasion."
        text="Every collection starts with a feeling: comfort, pride, courage, celebration. The objects come after."
      />
      <div className="featured-grid">
        <ProductCard product={products[0]!} large />
        <div className="featured-stack">
          <ProductCard product={products[1]!} index={1} />
          <ProductCard product={products[2]!} index={2} />
        </div>
      </div>
      <div className="section-link">
        <ButtonLink to="/shop" secondary>
          Explore every hamper
        </ButtonLink>
      </div>
    </section>
  );
}

function FoundersSection() {
  const reduced = useReducedMotion();
  return (
    <section className="founders-section">
      <div className="founders-image">
        <AmbientVideo
          src="/assets/founders.mp4"
          poster="/assets/founders-poster.jpg"
          alt="Two of the three Trio Atelier Co. creators together"
          descriptionId="founders-media-description"
        />
        <motion.div
          className="colour-edge"
          aria-hidden="true"
          initial={reduced ? false : { scaleY: 0 }}
          whileInView={{ scaleY: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9, ease: atelierEase }}
        />
      </div>
      <div className="founders-copy">
        <SectionIntro
          eyebrow="Behind each detail"
          title="Three creators. Three perspectives. One purpose."
          text="We built Trio Atelier around a simple belief: the most memorable gifts do more than arrive beautifully. They make someone feel known."
        />
        <motion.div
          className="pillars"
          variants={staggerContainer}
          initial={reduced ? false : "hidden"}
          whileInView="visible"
          viewport={revealViewport}
        >
          {[
            ["Curated", "Chosen with intention and care."],
            ["Created", "Beauty considered in every detail."],
            ["Connected", "Made for life’s meaningful moments."]
          ].map(([title, text]) => (
            <motion.div key={title} variants={staggerItem}>
              <Dots small />
              <span>
                <b>{title}</b>
                <small>{text}</small>
              </span>
            </motion.div>
          ))}
        </motion.div>
        <Reveal delay={0.12}>
          <ButtonLink to="/our-story" secondary>
            Meet the creators
          </ButtonLink>
        </Reveal>
      </div>
    </section>
  );
}

function ExperienceSection() {
  const reduced = useReducedMotion();
  const experienceRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: experienceRef, offset: ["start end", "center center"] });
  const captionY = useTransform(scrollYProgress, [0, 1], [18, 0]);
  return (
    <section className="experience-section" ref={experienceRef}>
      <div>
        <span className="giant-number">03</span>
        <SectionIntro
          light
          eyebrow="The Trio experience"
          title="The pause before the lid opens matters too."
          text="Tissue folded by hand. A note placed where it will be found first. Colour, texture and scent considered as one quiet story. We compose every box to make opening it a moment of its own."
        />
      </div>
      <div className="experience-image">
        <img src="/assets/celebration-hamper.webp" alt="Celebration hamper arranged in soft afternoon light" loading="lazy" />
        <motion.span style={reduced ? undefined : { y: captionY }}>Wrapped slowly, never rushed.</motion.span>
      </div>
    </section>
  );
}

function Testimonials() {
  const reduced = useReducedMotion();
  const notes = [
    [
      "“It didn’t feel like a hamper. It felt like someone had understood exactly what I needed that week.”",
      "— Hannah, York"
    ],
    [
      "“Every detail was so considered — even the note card felt like something I wanted to keep.”",
      "— Amara, London"
    ],
    [
      "“Quietly beautiful and genuinely personal. My sister called before she’d even finished opening it.”",
      "— Lucy, Leeds"
    ]
  ];
  return (
    <section className="section testimonials">
      <SectionIntro eyebrow="Notes we’ve kept" title="The feeling, received." />
      <motion.div
        className="note-grid"
        drag={reduced ? false : "x"}
        dragConstraints={{ left: -120, right: 120 }}
        dragElastic={0.08}
        variants={staggerContainer}
        initial={reduced ? false : "hidden"}
        whileInView="visible"
        viewport={revealViewport}
      >
        {notes.map(([quote, name], index) => (
          <motion.blockquote key={name} variants={staggerItem} style={{ rotate: `${index % 2 ? 1.1 : -1.2}deg` }} whileHover={reduced ? undefined : { y: -7, rotate: 0 }} transition={{ duration: motionDurations.quick }}>
            <Dots small />
            <p>{quote}</p>
            <cite>{name}</cite>
          </motion.blockquote>
        ))}
      </motion.div>
    </section>
  );
}

const newsletterSchema = z.object({
  email: z.string().email("Please enter a valid email address."),
  consent: z.literal(true, { message: "Please confirm you would like to join." })
});
type NewsletterValues = z.infer<typeof newsletterSchema>;

function Newsletter() {
  const reduced = useReducedMotion();
  const [complete, setComplete] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<NewsletterValues>({
    resolver: zodResolver(newsletterSchema),
    defaultValues: { email: "", consent: false as unknown as true }
  });
  return (
    <motion.section
      className="newsletter"
      variants={staggerContainer}
      initial={reduced ? false : "hidden"}
      whileInView="visible"
      viewport={revealViewport}
    >
      <motion.div variants={staggerItem}>
        <p className="eyebrow">A more thoughtful inbox</p>
        <h2>Notes on giving, noticing and saying what matters.</h2>
      </motion.div>
      {complete ? (
        <motion.div className="success-message" variants={staggerItem}>
          <Dots /> <b>You’re on the list.</b>
          <span>Thank you for making room for us.</span>
        </motion.div>
      ) : (
        <motion.form variants={staggerItem} onSubmit={handleSubmit((values) => { addNewsletterSignup(values.email); setComplete(true); })} noValidate>
          <label htmlFor="newsletter-email">Email address</label>
          <div className="input-row">
            <input
              id="newsletter-email"
              type="email"
              autoComplete="email"
              placeholder="you@example.co.uk"
              {...register("email")}
            />
            <button type="submit">Join the list ↗</button>
          </div>
          <AnimatePresence>{errors.email && <motion.span className="field-error" initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>{errors.email.message}</motion.span>}</AnimatePresence>
          <label className="check-label">
            <input type="checkbox" {...register("consent")} />
            <span>I agree to receive occasional emails and can unsubscribe any time.</span>
          </label>
          <AnimatePresence>{errors.consent && <motion.span className="field-error" initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>{errors.consent.message}</motion.span>}</AnimatePresence>
        </motion.form>
      )}
    </motion.section>
  );
}

function CorporateBand() {
  const reduced = useReducedMotion();
  return (
    <motion.section
      className="corporate-band"
      initial={reduced ? false : { opacity: 0, clipPath: "inset(0 0 24% 0)" }}
      whileInView={{ opacity: 1, clipPath: "inset(0 0 0% 0)" }}
      viewport={revealViewport}
      transition={revealTransition}
    >
      <motion.div initial={reduced ? false : { y: 22 }} whileInView={{ y: 0 }} viewport={revealViewport} transition={revealTransition}>
        <p className="eyebrow">For clients, teams and remarkable milestones</p>
        <h2>Gifting that feels personal, even at scale.</h2>
      </motion.div>
      <ButtonLink to="/corporate-gifting" secondary>
        Begin a conversation
      </ButtonLink>
    </motion.section>
  );
}

function JournalPreview() {
  const reduced = useReducedMotion();
  return (
    <section className="section journal-preview">
      <SectionIntro eyebrow="From the journal" title="A few words for the moments that need them." />
      <div className="journal-grid">
        {journalPosts.map((post, index) => (
          <motion.article
            key={post.slug}
            initial={reduced ? false : { opacity: 0, x: index % 2 ? 28 : -28 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={revealViewport}
            transition={{ ...revealTransition, delay: index * 0.08 }}
          >
            <span className="journal-number">0{index + 1}</span>
            <p className="eyebrow">{post.category}</p>
            <h3>
              <Link to={`/journal/${post.slug}`}>{post.title}</Link>
            </h3>
            <p>{post.excerpt}</p>
            <Link className="text-link" to={`/journal/${post.slug}`}>
              Read the note
            </Link>
          </motion.article>
        ))}
      </div>
    </section>
  );
}

function HomePage() {
  return (
    <>
      <Hero />
      <ValuesStrip />
      <FeaturedCollections />
      <FoundersSection />
      <ExperienceSection />
      <Testimonials />
      <CorporateBand />
      <JournalPreview />
      <Newsletter />
      <Reveal className="final-cta" direction="scale">
          <Dots />
          <h2>Send a gift that says what words can’t.</h2>
          <ButtonLink to="/shop">Find their gift</ButtonLink>
      </Reveal>
    </>
  );
}

function PageHero({
  eyebrow,
  title,
  text
}: {
  eyebrow: string;
  title: string;
  text: string;
}) {
  const reduced = useReducedMotion();
  return (
    <motion.section
      className="page-hero"
      variants={staggerContainer}
      initial={reduced ? false : "hidden"}
      animate="visible"
    >
      <motion.p className="eyebrow" variants={staggerItem}>
        <Dots small /> {eyebrow}
      </motion.p>
      <motion.h1 variants={staggerItem}>{title}</motion.h1>
      <motion.p variants={staggerItem}>{text}</motion.p>
    </motion.section>
  );
}

function ShopPage() {
  const [filter, setFilter] = useState("All");
  const reduced = useReducedMotion();
  const moods = ["All", "Celebration", "Comfort", "New beginnings"];
  const filtered = filter === "All" ? products : products.filter((product) => product.mood === filter);
  return (
    <>
      <PageHero
        eyebrow="The collection"
        title="Choose for the feeling you want to send."
        text="Not sure what the occasion calls for? Start with how you want them to feel when the lid opens."
      />
      <section className="shop-shell">
        <motion.div
          className="filter-row"
          aria-label="Filter products"
          initial={reduced ? false : { opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={revealTransition}
        >
          {moods.map((mood) => (
            <button
              type="button"
              key={mood}
              className={filter === mood ? "active" : ""}
              aria-pressed={filter === mood}
              onClick={() => setFilter(mood)}
            >
              {mood}
            </button>
          ))}
        </motion.div>
        <motion.p className="result-count" initial={reduced ? false : { opacity: 0 }} animate={{ opacity: 1 }} transition={{ ...revealTransition, delay: 0.1 }}>{filtered.length} considered gifts</motion.p>
        <div className="shop-grid">
          {filtered.map((product, index) => <ProductCard key={product.slug} product={product} index={index} />)}
        </div>
      </section>
      <CorporateBand />
    </>
  );
}

function ProductPage() {
  const { slug } = useParams();
  const product = products.find((item) => item.slug === slug);
  const { add } = useCart();
  const [note, setNote] = useState("");
  const [added, setAdded] = useState(false);
  const reduced = useReducedMotion();
  if (!product) return <NotFoundPage />;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    add(product);
    setAdded(true);
  };
  return (
    <section className="product-page">
      <div className="product-page-image">
        <img src={product.image} alt={`${product.name} curated gift hamper`} />
        <motion.span
          className="product-image-index"
          initial={reduced ? false : { opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...revealTransition, delay: 0.2 }}
        >
          01 / considered in Leeds
        </motion.span>
      </div>
      <motion.div
        className="product-page-copy"
        variants={staggerContainer}
        initial={reduced ? false : "hidden"}
        animate="visible"
      >
        <motion.p className="eyebrow" variants={staggerItem}>{product.eyebrow}</motion.p>
        <motion.h1 variants={staggerItem}>{product.name}</motion.h1>
        <motion.span className="product-price" variants={staggerItem}>£{product.price}.00</motion.span>
        <motion.p className="product-description" variants={staggerItem}>{product.description}</motion.p>
        <motion.div className="contents-list" variants={staggerItem}>
          <h2>Inside, you’ll find</h2>
          {product.contents.map((item, index) => (
            <motion.span key={item} initial={reduced ? false : { opacity: 0, x: -12 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.045, ease: atelierEase }}>
              <Dots small /> {item}
            </motion.span>
          ))}
        </motion.div>
        <motion.form variants={staggerItem} onSubmit={submit}>
          <label htmlFor="gift-note">Your gift note <span>optional</span></label>
          <textarea
            id="gift-note"
            value={note}
            maxLength={240}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Write what you’d like them to read first…"
          />
          <small>{note.length}/240 · Included with your gift</small>
          <button className="button full-button" type="submit">
            {added ? "Added to your basket ·" : `Add to basket — £${product.price}`}
          </button>
        </motion.form>
        <details>
          <summary>Delivery & presentation</summary>
          <p>
            Prepared in Leeds and sent in a protective outer carton. Standard tracked UK delivery is £4.95,
            complimentary over £75.
          </p>
        </details>
        <details>
          <summary>Ingredients & allergens</summary>
          <p>
            Placeholder product content: confirm all ingredients and allergens before ordering. Our shared
            studio cannot guarantee an allergen-free environment.
          </p>
        </details>
      </motion.div>
    </section>
  );
}

function BuilderPage() {
  const [selected, setSelected] = useState<string[]>(["tea", "candle", "chocolate"]);
  const [colour, setColour] = useState("Sage");
  const [submitted, setSubmitted] = useState(false);
  const reduced = useReducedMotion();
  const basePrice = 18;
  const itemTotal = builderItems
    .filter((item) => selected.includes(item.id))
    .reduce((sum, item) => sum + item.price, 0);
  const total = basePrice + itemTotal;
  const toggle = (id: string) =>
    setSelected((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    );
  const submitBuild = () => {
    addBuilderSubmission({
      items: builderItems.filter((item) => selected.includes(item.id)).map((item) => item.name),
      colour,
      total
    });
    setSubmitted(true);
  };

  return (
    <>
      <PageHero
        eyebrow="Made by you, finished by us"
        title="Build a hamper around who they are."
        text="Begin with the box, choose the pieces that sound like them, then leave the final composition to us."
      />
      <section className="builder">
        <div className="builder-options">
          <div className="builder-step">
            <span>01</span>
            <div>
              <h2>Choose their pieces</h2>
              <p>Select at least three. We’ll make sure the finished arrangement feels balanced.</p>
            </div>
          </div>
          <motion.div
            className="builder-items"
            variants={staggerContainer}
            initial={reduced ? false : "hidden"}
            whileInView="visible"
            viewport={revealViewport}
          >
            {builderItems.map((item) => {
              const active = selected.includes(item.id);
              return (
                <motion.button
                  type="button"
                  key={item.id}
                  className={active ? "active" : ""}
                  onClick={() => toggle(item.id)}
                  aria-pressed={active}
                  variants={staggerItem}
                  whileHover={reduced ? undefined : { y: -4 }}
                >
                  <span className="builder-check">{active ? "✓" : "+"}</span>
                  <small>{item.category}</small>
                  <b>{item.name}</b>
                  <em>+£{item.price}</em>
                </motion.button>
              );
            })}
          </motion.div>
          <div className="builder-step">
            <span>02</span>
            <div>
              <h2>Choose the finishing colour</h2>
              <p>We’ll carry it through the ribbon, tissue and gift-note details.</p>
              <div className="swatches">
                {["Dusty rose", "Sage", "Antique gold"].map((name) => (
                  <button
                    type="button"
                    key={name}
                    className={`${name.toLowerCase().replace(" ", "-")} ${colour === name ? "active" : ""}`}
                    onClick={() => setColour(name)}
                    aria-label={name}
                    aria-pressed={colour === name}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
        <aside className="builder-summary">
          <div className="builder-photo">
            <img src="/assets/hero-hamper.webp" alt="Example custom hamper arrangement" />
            <motion.span
              key={colour}
              initial={reduced ? false : { opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: motionDurations.quick }}
            >
              {colour} finish
            </motion.span>
          </div>
          <motion.div
            initial={reduced ? false : { opacity: 0, x: 28 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={revealViewport}
            transition={revealTransition}
          >
            <p className="eyebrow">Your hamper</p>
            <h2>A gift, composed for them.</h2>
            <motion.ul layout>
              <li><span>Signature hamper & finishing</span><b>£{basePrice}</b></li>
              <AnimatePresence initial={false}>
                {builderItems.filter((item) => selected.includes(item.id)).map((item) => (
                  <motion.li key={item.id} layout initial={reduced ? false : { opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 10 }} transition={{ duration: motionDurations.quick }}><span>{item.name}</span><b>£{item.price}</b></motion.li>
                ))}
              </AnimatePresence>
            </motion.ul>
            <div className="builder-total"><span>Estimated total</span><AnimatePresence mode="wait" initial={false}><motion.b key={total} initial={reduced ? false : { opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>£{total}</motion.b></AnimatePresence></div>
            <button className="button full-button" type="button" disabled={selected.length < 3} onClick={submitBuild}>
              {submitted ? "Composition saved · ✓" : selected.length < 3 ? "Choose at least three pieces" : "Continue with this hamper ↗"}
            </button>
            <small>Pricing and availability shown for demonstration.</small>
          </motion.div>
        </aside>
      </section>
    </>
  );
}

function OurStoryPage() {
  const reduced = useReducedMotion();
  return (
    <>
      <PageHero
        eyebrow="Our story"
        title="A shared instinct for making people feel seen."
        text="Trio Atelier Co. began with three perspectives and one recurring thought: gifting should feel more human."
      />
      <section className="story-lead">
        <div>
          <img src="/assets/founders-poster.jpg" alt="Two of the three Trio Atelier Co. founders" />
        </div>
        <motion.div
          variants={staggerContainer}
          initial={reduced ? false : "hidden"}
          whileInView="visible"
          viewport={revealViewport}
        >
          <motion.p className="eyebrow" variants={staggerItem}>The creators</motion.p>
          <motion.h2 variants={staggerItem}>We notice different things. That’s the point.</motion.h2>
          <motion.p variants={staggerItem}>
            One of us begins with colour, one with the words on the card, one with the smallest practical detail.
            Together, those instincts become gifts with depth: beautiful, useful and emotionally exact.
          </motion.p>
          <motion.p variants={staggerItem}>
            We prepare each hamper in Leeds, choosing every piece for the role it plays in the whole. No filler.
            No noise. Just a thoughtful composition that arrives ready to say something true.
          </motion.p>
          <Dots />
        </motion.div>
      </section>
      <section className="values-editorial">
        {[
          ["01", "Thoughtfulness", "We begin with the person receiving it: their season, their needs, their particular joy."],
          ["02", "Intention", "Every object earns its place, and every detail contributes to the feeling of the whole."],
          ["03", "Quality", "We choose fewer, better things from makers whose care can be felt in the finished piece."],
          ["04", "The experience", "The reveal is part of the gift. We compose each layer to be opened slowly."],
          ["05", "Emotion", "A beautiful object matters most when it carries a message someone needed to hear."]
        ].map(([number, title, text]) => (
          <motion.article
            key={number}
            initial={reduced ? false : { opacity: 0, x: Number(number) % 2 ? -26 : 26 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={revealViewport}
            transition={revealTransition}
          >
            <span>{number}</span>
            <h3>{title}</h3>
            <p>{text}</p>
          </motion.article>
        ))}
      </section>
      <CorporateBand />
    </>
  );
}

const enquirySchema = z.object({
  name: z.string().min(2, "Please tell us your name."),
  email: z.string().email("Please enter a valid email address."),
  company: z.string().optional(),
  message: z.string().min(20, "Please share a little more so we can help."),
  consent: z.literal(true, { message: "Please confirm we may reply to your enquiry." })
});
type EnquiryValues = z.infer<typeof enquirySchema>;

function EnquiryForm({ corporate = false }: { corporate?: boolean }) {
  const [complete, setComplete] = useState(false);
  const reduced = useReducedMotion();
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<EnquiryValues>({
    resolver: zodResolver(enquirySchema),
    defaultValues: { consent: false as unknown as true }
  });
  if (complete)
    return (
      <div className="form-success">
        <Dots />
        <h2>Thank you — we’ve got it.</h2>
        <p>We’ll read your note carefully and reply within two working days.</p>
      </div>
    );
  return (
    <motion.form
      className="enquiry-form"
      initial={reduced ? false : { opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={revealViewport}
      transition={revealTransition}
      onSubmit={handleSubmit((values) => {
      addEnquiry({
        kind: corporate ? "corporate" : "contact",
        name: values.name,
        email: values.email,
        company: values.company,
        message: values.message
      });
      setComplete(true);
      })}
      noValidate
    >
      <div className="field-pair">
        <label>
          Your name
          <input autoComplete="name" {...register("name")} />
          {errors.name && <span className="field-error">{errors.name.message}</span>}
        </label>
        <label>
          Email address
          <input type="email" autoComplete="email" {...register("email")} />
          {errors.email && <span className="field-error">{errors.email.message}</span>}
        </label>
      </div>
      {corporate && (
        <label>
          Company name
          <input autoComplete="organization" {...register("company")} />
        </label>
      )}
      <label>
        {corporate ? "Tell us about the people and the moment" : "How can we help?"}
        <textarea rows={6} {...register("message")} />
        {errors.message && <span className="field-error">{errors.message.message}</span>}
      </label>
      <label className="check-label">
        <input type="checkbox" {...register("consent")} />
        <span>
          I agree to Trio Atelier Co. contacting me about this enquiry. See our{" "}
          <Link to="/privacy-policy">Privacy Policy</Link>.
        </span>
      </label>
      {errors.consent && <span className="field-error">{errors.consent.message}</span>}
      <button className="button" type="submit">Send enquiry ↗</button>
    </motion.form>
  );
}

function CorporatePage() {
  const reduced = useReducedMotion();
  return (
    <>
      <PageHero
        eyebrow="Corporate gifting"
        title="Personal enough to be remembered. Seamless enough to scale."
        text="From ten carefully considered thank-yous to a nationwide client moment, we bring warmth and detail to business gifting."
      />
      <section className="corporate-details">
        <div>
          <p className="eyebrow">What we can create</p>
          <h2>A gift that feels like your values, not your logo.</h2>
          <div className="service-list">
            {[
              ["Client appreciation", "Make the relationship feel noticed, at renewal or simply because."],
              ["Team milestones", "Welcome, recognise or celebrate people with something genuinely personal."],
              ["Events & launches", "A considered physical moment that extends the story beyond the room."],
              ["Bespoke commissions", "Custom contents, branded notes and coordinated delivery for one clear purpose."]
            ].map(([title, text], index) => (
              <motion.div
                key={title}
                initial={reduced ? false : { opacity: 0, x: -24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={revealViewport}
                transition={{ ...revealTransition, delay: index * 0.07 }}
              >
                <span>0{index + 1}</span><h3>{title}</h3><p>{text}</p>
              </motion.div>
            ))}
          </div>
        </div>
        <motion.div
          className="corporate-form-wrap"
          initial={reduced ? false : { opacity: 0, x: 32 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={revealViewport}
          transition={revealTransition}
        >
          <p className="eyebrow">Start the conversation</p>
          <h2>Tell us what you’re hoping to say.</h2>
          <EnquiryForm corporate />
        </motion.div>
      </section>
    </>
  );
}

function JournalPage() {
  const reduced = useReducedMotion();
  return (
    <>
      <PageHero
        eyebrow="The journal"
        title="On giving well, finding the words and noticing more."
        text="Thoughtful guides for life’s less obvious gifting moments, written from our Leeds studio."
      />
      <section className="journal-list">
        {journalPosts.map((post, index) => (
          <motion.article
            key={post.slug}
            initial={reduced ? false : { opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={revealViewport}
            transition={{ ...revealTransition, delay: index * 0.06 }}
          >
            <span className="journal-number">0{index + 1}</span>
            <div>
              <p className="eyebrow">{post.category} · {post.date}</p>
              <h2><Link to={`/journal/${post.slug}`}>{post.title}</Link></h2>
              <p>{post.excerpt}</p>
              <Link className="text-link" to={`/journal/${post.slug}`}>Read the note</Link>
            </div>
          </motion.article>
        ))}
      </section>
      <Newsletter />
    </>
  );
}

function JournalArticlePage() {
  const { slug } = useParams();
  const post = journalPosts.find((item) => item.slug === slug) ?? journalPosts[0]!;
  const reduced = useReducedMotion();
  const articleReveal = reduced
    ? { initial: false as const }
    : {
        initial: { opacity: 0, y: 24 },
        whileInView: { opacity: 1, y: 0 },
        viewport: revealViewport,
        transition: revealTransition
      };
  return (
    <article className="article-page">
      <motion.header variants={staggerContainer} initial={reduced ? false : "hidden"} animate="visible">
        <motion.p className="eyebrow" variants={staggerItem}>{post.category} · {post.date}</motion.p>
        <motion.h1 variants={staggerItem}>{post.title}</motion.h1>
        <motion.p variants={staggerItem}>{post.excerpt}</motion.p>
      </motion.header>
      <div className="article-body">
        <motion.p className="drop-cap" {...articleReveal}>
          The best gifts do not try to fix the moment. They simply say: I noticed, I’m here, and you do not have to
          explain everything for me to care.
        </motion.p>
        <motion.h2 {...articleReveal}>Begin with what is true</motion.h2>
        <motion.p {...articleReveal}>
          Before choosing an object, name what you want the person to feel. Comforted? Celebrated? Reminded of their
          own courage? That answer is a better guide than the occasion printed on a card.
        </motion.p>
        <motion.blockquote {...articleReveal}>Specific attention is what turns a beautiful thing into a meaningful gift.</motion.blockquote>
        <motion.h2 {...articleReveal}>Choose fewer things, more carefully</motion.h2>
        <motion.p {...articleReveal}>
          A useful ritual, something delicious and one piece they might keep is often enough. Each item should have a
          reason to be there, and together they should feel like one calm thought.
        </motion.p>
        <motion.h2 {...articleReveal}>Write the note you nearly didn’t</motion.h2>
        <motion.p {...articleReveal}>
          You do not need to solve anything. Say what you admire, remember one precise detail, or simply give them
          permission to feel exactly as they do. The truest sentence is usually the one worth keeping.
        </motion.p>
        <Dots />
        <ButtonLink to="/shop">Find a thoughtful gift</ButtonLink>
      </div>
    </article>
  );
}

function FaqPage() {
  const reduced = useReducedMotion();
  return (
    <>
      <PageHero
        eyebrow="A few useful details"
        title="Questions, answered thoughtfully."
        text="Everything you need to know about delivery, gift notes, allergens and how each hamper arrives."
      />
      <section className="faq-list">
        {faqs.map((faq, index) => (
          <motion.details
            key={faq.question}
            initial={reduced ? false : { opacity: 0, x: index % 2 ? 22 : -22 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={revealViewport}
            transition={{ ...revealTransition, delay: (index % 4) * 0.05 }}
          >
            <summary><span>0{index + 1}</span>{faq.question}</summary>
            <p>{faq.answer}</p>
          </motion.details>
        ))}
      </section>
      <CorporateBand />
    </>
  );
}

function ContactPage() {
  const reduced = useReducedMotion();
  return (
    <>
      <PageHero
        eyebrow="Contact"
        title="There’s a person behind every message."
        text="Ask about a gift, a bespoke idea, or something you can’t quite find on the site. We’d be glad to help."
      />
      <section className="contact-layout">
        <motion.div
          className="contact-details"
          initial={reduced ? false : { opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={revealViewport}
          transition={revealTransition}
        >
          <p className="eyebrow">Come and say hello</p>
          <a href="mailto:info@trioatelierco.co.uk">info@trioatelierco.co.uk</a>
          <a href="tel:+447741848404">+44 7741 848404</a>
          <p>Leeds, West Yorkshire<br />Shipping throughout the UK</p>
          <a
            className="text-link"
            href="https://wa.me/447741848404?text=Hi%20Trio%20Atelier%20Co.%20I%27d%20love%20to%20talk%20through%20a%20bespoke%20hamper."
            target="_blank"
            rel="noreferrer"
          >
            Message us on WhatsApp ↗
          </a>
        </motion.div>
        <EnquiryForm />
      </section>
    </>
  );
}

function CartPage() {
  const { lines, total, change, remove } = useCart();
  const reduced = useReducedMotion();
  const delivery = total >= 75 || total === 0 ? 0 : 4.95;
  return (
    <section className="cart-page">
      <PageHero
        eyebrow="Your basket"
        title={lines.length ? "A thoughtful gift is taking shape." : "Your basket is waiting quietly."}
        text={lines.length ? "Review the details before continuing." : "Begin with the feeling you want to send."}
      />
      {lines.length === 0 ? (
        <div className="empty-cart">
          <Dots />
          <ButtonLink to="/shop">Explore the collection</ButtonLink>
        </div>
      ) : (
        <div className="cart-layout">
          <div className="cart-lines">
            {lines.map((line, index) => (
              <article key={line.product.slug}>
                <img src={line.product.image} alt="" />
                <motion.div
                  className="cart-line-details"
                  initial={reduced ? false : { opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ ...revealTransition, delay: index * 0.06 }}
                >
                  <p className="eyebrow">{line.product.eyebrow}</p>
                  <h2>{line.product.name}</h2>
                  <span className="cart-line-price">£{line.product.price}.00</span>
                  <div className="cart-line-actions">
                    <div className="quantity" role="group" aria-label={`Quantity for ${line.product.name}`}>
                      <button type="button" onClick={() => change(line.product.slug, line.quantity - 1)} aria-label="Decrease quantity">−</button>
                      <span>{line.quantity}</span>
                      <button type="button" onClick={() => change(line.product.slug, line.quantity + 1)} aria-label="Increase quantity">+</button>
                    </div>
                    <button className="remove-button" type="button" onClick={() => remove(line.product.slug)}>Remove</button>
                  </div>
                </motion.div>
              </article>
            ))}
          </div>
          <aside className="order-summary">
            <p className="eyebrow">Order summary</p>
            <div><span>Subtotal</span><b>£{total.toFixed(2)}</b></div>
            <div><span>Tracked UK delivery</span><b>{delivery ? `£${delivery}` : "Complimentary"}</b></div>
            <div className="summary-total"><span>Total</span><b>£{(total + delivery).toFixed(2)}</b></div>
            <Link className="button full-button" to="/checkout">Continue to checkout ↗</Link>
            <small>Secure checkout · Gift note included · UK delivery</small>
          </aside>
        </div>
      )}
    </section>
  );
}

function CheckoutPage() {
  const { lines, total, clear } = useCart();
  const navigate = useNavigate();
  const reduced = useReducedMotion();
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const order = createOrder({
      items: lines.map((line) => ({ name: line.product.name, quantity: line.quantity, price: line.product.price })),
      total,
      customer: {
        name: `${String(data.get("firstName") ?? "")} ${String(data.get("lastName") ?? "")}`.trim(),
        email: String(data.get("email") ?? "")
      },
      delivery: {
        address: String(data.get("address") ?? ""),
        city: String(data.get("city") ?? ""),
        postcode: String(data.get("postcode") ?? ""),
        preferredDate: String(data.get("preferredDate") ?? "") || undefined
      },
      giftNote: String(data.get("giftNote") ?? "") || undefined
    });
    clear();
    navigate("/order-confirmation", { state: { orderId: order.id } });
  };
  if (!lines.length) return <CartPage />;
  return (
    <section className="checkout-page">
      <PageHero
        eyebrow="Checkout"
        title="Where should this feeling arrive?"
        text="Demo checkout: no payment will be taken. Live Stripe processing requires production credentials."
      />
      <motion.form
        className="checkout-form"
        onSubmit={submit}
        initial={reduced ? false : { opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={revealTransition}
      >
        <motion.div className="checkout-fields" variants={staggerContainer} initial={reduced ? false : "hidden"} animate="visible">
          <h2>Recipient details</h2>
          <motion.div className="field-pair" variants={staggerItem}>
            <label>First name<input name="firstName" required autoComplete="given-name" /></label>
            <label>Last name<input name="lastName" required autoComplete="family-name" /></label>
          </motion.div>
          <motion.label variants={staggerItem}>Email address<input name="email" type="email" required autoComplete="email" /></motion.label>
          <motion.label variants={staggerItem}>Address line 1<input name="address" required autoComplete="address-line1" /></motion.label>
          <motion.div className="field-pair" variants={staggerItem}>
            <label>Town or city<input name="city" required autoComplete="address-level2" /></label>
            <label>Postcode<input name="postcode" required autoComplete="postal-code" /></label>
          </motion.div>
          <motion.label variants={staggerItem}>Preferred delivery date<input name="preferredDate" type="date" /></motion.label>
          <motion.label variants={staggerItem}>Gift note<textarea name="giftNote" rows={4} maxLength={240} /></motion.label>
        </motion.div>
        <aside className="order-summary">
          <p className="eyebrow">Your gift</p>
          {lines.map((line) => <div key={line.product.slug}><span>{line.quantity} × {line.product.name}</span><b>£{line.quantity * line.product.price}</b></div>)}
          <div className="summary-total"><span>Demo total</span><b>£{total.toFixed(2)}</b></div>
          <button className="button full-button" type="submit">Place demo order ↗</button>
        </aside>
      </motion.form>
    </section>
  );
}

function ConfirmationPage() {
  const { state } = useLocation();
  const orderId = (state as { orderId?: string } | null)?.orderId ?? localStorage.getItem("trio-last-order");
  return (
    <section className="confirmation-page">
      <Dots />
      <p className="eyebrow">Order received</p>
      <h1>It’s in thoughtful hands now.</h1>
      <p>This demonstration order has been recorded locally only. No payment was taken and no fulfilment was created.</p>
      {orderId && <p className="confirmation-reference">Order reference · <b>{orderId}</b></p>}
      <ButtonLink to="/shop" secondary>Continue browsing</ButtonLink>
    </section>
  );
}

const policies: Record<string, { eyebrow: string; title: string; sections: [string, string][] }> = {
  "/privacy-policy": {
    eyebrow: "Legal",
    title: "Privacy policy",
    sections: [
      ["The information we collect", "We collect only the details needed to answer enquiries, fulfil orders and, where you explicitly opt in, send thoughtful marketing emails."],
      ["How we use it", "Order and contact information is used for fulfilment, customer support, fraud prevention and legal record-keeping. We do not sell personal information."],
      ["Your rights", "Under UK GDPR you can ask to access, correct, export or erase eligible personal information. Email info@trioatelierco.co.uk to make a request."],
      ["Retention", "Enquiry records are reviewed annually. Transaction records are retained only for the periods required by tax, accounting and consumer law."]
    ]
  },
  "/cookie-policy": {
    eyebrow: "Legal",
    title: "Cookie policy",
    sections: [
      ["Necessary cookies", "These remember your basket, theme and privacy choices. The site cannot work as expected without them."],
      ["Optional cookies", "Analytics and marketing cookies remain off unless you choose to allow them. This demonstration currently loads no optional trackers."],
      ["Changing your choice", "Clear this site’s storage in your browser to show the consent panel again, or contact us if you need help."]
    ]
  },
  "/terms-and-conditions": {
    eyebrow: "Legal",
    title: "Terms & conditions",
    sections: [
      ["About these terms", "These terms govern orders placed with Trio Atelier Co. and should be reviewed by a qualified UK legal adviser before commerce launch."],
      ["Products and pricing", "Photography, contents, availability, VAT status and prices must be confirmed before accepting live orders. Substitutions will only be of equal or greater value."],
      ["Orders", "An order is accepted only when payment is confirmed and we send an order confirmation. We may decline or refund an order we cannot safely fulfil."],
      ["Liability", "Nothing in these terms limits rights or remedies that cannot lawfully be excluded under UK consumer law."]
    ]
  },
  "/returns-policy": {
    eyebrow: "Useful details",
    title: "Returns & refunds",
    sections: [
      ["If something isn’t right", "Contact us within 48 hours of delivery with your order number and photographs of any damage or incorrect items."],
      ["Perishable and personalised goods", "Some food, drink, hygiene and personalised items may not qualify for change-of-mind returns. Your statutory rights for faulty goods remain unaffected."],
      ["Refund timing", "Approved refunds are returned to the original payment method. Your bank may take several working days to display the credit."]
    ]
  },
  "/delivery-information": {
    eyebrow: "Useful details",
    title: "Delivery information",
    sections: [
      ["UK-wide delivery", "We currently deliver within the UK from our Leeds studio. Standard tracked delivery is typically 2–4 working days after dispatch."],
      ["Future dates", "You can request a preferred arrival date at checkout. We will time dispatch carefully, but courier delays mean exact dates cannot be guaranteed."],
      ["Recipient deliveries", "Send directly to the recipient using their delivery address and your details for billing. No prices are included inside the parcel."]
    ]
  }
};

function PolicyPage({ path }: { path: string }) {
  const policy = policies[path]!;
  const reduced = useReducedMotion();
  return (
    <>
      <PageHero eyebrow={policy.eyebrow} title={policy.title} text="Clear, plain-language information about how we work." />
      <section className="policy-content">
        <p className="policy-notice">Draft launch copy — obtain legal review before enabling live commerce.</p>
        {policy.sections.map(([title, text], index) => (
          <motion.div
            key={title}
            initial={reduced ? false : { opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={revealViewport}
            transition={{ ...revealTransition, delay: (index % 3) * 0.06 }}
          >
            <h2>{title}</h2><p>{text}</p>
          </motion.div>
        ))}
      </section>
    </>
  );
}

function NotFoundPage() {
  return (
    <section className="confirmation-page">
      <Dots />
      <p className="eyebrow">404</p>
      <h1>This page has wandered off.</h1>
      <ButtonLink to="/">Return home</ButtonLink>
    </section>
  );
}

function CookieConsent() {
  const [visible, setVisible] = useState(() => !localStorage.getItem("trio-consent"));
  const [details, setDetails] = useState(false);
  const save = (choice: string) => {
    localStorage.setItem("trio-consent", choice);
    setVisible(false);
  };
  if (!visible) return null;
  return (
    <aside className="cookie-panel" aria-label="Cookie choices">
      <Dots small />
      <h2>A small note about cookies.</h2>
      <p>We use necessary cookies to remember your basket and preferences. Optional analytics stays off unless you allow it.</p>
      {details && (
        <div className="cookie-details">
          <span><b>Necessary</b><small>Always on</small></span>
          <span><b>Analytics</b><small>Off until allowed</small></span>
        </div>
      )}
      <div>
        <button type="button" onClick={() => save("necessary")}>Necessary only</button>
        <button type="button" onClick={() => setDetails((current) => !current)}>Manage</button>
        <button className="accept" type="button" onClick={() => save("all")}>Allow all</button>
      </div>
    </aside>
  );
}

function Footer() {
  return (
    <footer>
      <div className="footer-top">
        <div className="footer-brand">
          <Logo />
          <p>Three creators. Three perspectives.<br />One beautiful experience.</p>
          <Dots />
        </div>
        <div>
          <p className="footer-heading">Explore</p>
          <Link to="/shop">The collection</Link>
          <Link to="/build-a-hamper">Build a hamper</Link>
          <Link to="/our-story">Our story</Link>
          <Link to="/journal">Journal</Link>
        </div>
        <div>
          <p className="footer-heading">Help</p>
          <Link to="/faq">FAQ</Link>
          <Link to="/delivery-information">Delivery</Link>
          <Link to="/returns-policy">Returns</Link>
          <Link to="/contact">Contact</Link>
        </div>
        <div>
          <p className="footer-heading">Find us</p>
          <a href="mailto:info@trioatelierco.co.uk">info@trioatelierco.co.uk</a>
          <a href="tel:+447741848404">+44 7741 848404</a>
          <span>Leeds, West Yorkshire</span>
          <a href="https://instagram.com/trioatelierco" target="_blank" rel="noreferrer">Instagram ↗</a>
        </div>
      </div>
      <div className="footer-bottom">
        <span>© {new Date().getFullYear()} Trio Atelier Co.</span>
        <div>
          <Link to="/privacy-policy">Privacy</Link>
          <Link to="/cookie-policy">Cookies</Link>
          <Link to="/terms-and-conditions">Terms</Link>
        </div>
        {/* Removable on client request per Tier 4 terms. */}
        <a href="https://instagram.com/buildwithduke" target="_blank" rel="noreferrer">Website design by buildwithduke</a>
      </div>
    </footer>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  return (
    <div className="scroll-progress" aria-hidden="true">
      <motion.span style={{ scaleX: scrollYProgress }} />
    </div>
  );
}

function Metadata() {
  const { pathname } = useLocation();
  useEffect(() => {
    const routeTitle =
      pathname === "/"
        ? "Curated gift hampers"
        : pathname
            .split("/")
            .filter(Boolean)
            .pop()
            ?.replaceAll("-", " ")
            .replace(/\b\w/g, (letter) => letter.toUpperCase()) ?? "Curated gift hampers";
    document.title = `${routeTitle} — Trio Atelier Co.`;
  }, [pathname]);
  return null;
}

function AtelierPrelude() {
  const reduced = useReducedMotion();
  const [visible, setVisible] = useState(() => !reduced && sessionStorage.getItem("trio-intro-seen") !== "yes");
  useEffect(() => {
    if (!visible) return;
    sessionStorage.setItem("trio-intro-seen", "yes");
    const timer = window.setTimeout(() => setVisible(false), 920);
    return () => window.clearTimeout(timer);
  }, [visible]);
  return (
    <AnimatePresence>
      {visible && (
        <motion.div className="atelier-prelude" initial={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.28 }} aria-hidden="true">
          <div>
            <img src="/apple-touch-icon.png" alt="" />
            <motion.span initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.52, ease: atelierEase }}>
              <Dots />
            </motion.span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function StorefrontApp() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");

  if (isAdmin) {
    return (
      <>
        <ScrollToTop />
        <Metadata />
        <Routes>
          <Route path="/admin/*" element={<AdminApp />} />
        </Routes>
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <ScrollProgress />
      <Metadata />
      <AtelierPrelude />
      <Header />
      <main id="main">
        <Routes location={location}>
          <Route path="/" element={<HomePage />} />
          <Route path="/shop" element={<ShopPage />} />
          <Route path="/shop/product/:slug" element={<ProductPage />} />
          <Route path="/build-a-hamper" element={<BuilderPage />} />
          <Route path="/our-story" element={<OurStoryPage />} />
          <Route path="/corporate-gifting" element={<CorporatePage />} />
          <Route path="/journal" element={<JournalPage />} />
          <Route path="/journal/:slug" element={<JournalArticlePage />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/order-confirmation" element={<ConfirmationPage />} />
          {Object.keys(policies).map((path) => <Route key={path} path={path} element={<PolicyPage path={path} />} />)}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
      <Footer />
      <CookieConsent />
    </>
  );
}

function App() {
  return (
    <CartProvider>
      <StorefrontApp />
    </CartProvider>
  );
}

export default App;
